import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus, Save, X, Settings } from 'lucide-react';
import SuperAdminLayout from '../components/SuperAdminLayout';
import toast from 'react-hot-toast';

const ManageSaasPlans = ({ token }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPlan, setEditingPlan] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        id: 0,
        plan_name: '',
        tagline: '',
        is_monthly_active: 1,
        price_monthly: '',
        days_monthly: 30,
        is_yearly_active: 1,
        price_yearly: '',
        days_yearly: 365,
        max_staff: 2,
        ai_credits: 0,
        max_orders: 100,
        has_whatsapp: 0,
        has_pos: 0,
        has_coupons: 0,
        has_push: 0,
        has_loyalty_system: 0,
        is_popular: 0,
        features_included_json: '[]',
        features_excluded_json: '[]'
    });

    const [includedList, setIncludedList] = useState('');
    const [excludedList, setExcludedList] = useState('');

    useEffect(() => {
        fetchPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await fetch('https://menu.searchisend.in/superadmin/v2/v2_manage_plans.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'fetch_all' })
            });
            const data = await res.json();
            if (data.success) {
                setPlans(data.plans);
            } else {
                toast.error(data.message || "Failed to load plans");
            }
        } catch (err) {
            toast.error("Error connecting to server");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (plan = null) => {
        if (plan) {
            setFormData(plan);
            setIncludedList(JSON.parse(plan.features_included_json || '[]').join('\n'));
            setExcludedList(JSON.parse(plan.features_excluded_json || '[]').join('\n'));
            setEditingPlan(plan);
        } else {
            setFormData({
                id: 0,
                plan_name: '',
                tagline: '',
                is_monthly_active: 1,
                price_monthly: '',
                days_monthly: 30,
                is_yearly_active: 1,
                price_yearly: '',
                days_yearly: 365,
                max_staff: 2,
                ai_credits: 0,
                max_orders: 100,
                has_whatsapp: 0,
                has_pos: 0,
                has_coupons: 0,
                has_push: 0,
                has_loyalty_system: 0,
                has_offer_popup: 0,
                has_staff_management: 0,
                has_ad_monetization: 0,
                is_popular: 0,
                features_included_json: '[]',
                features_excluded_json: '[]'
            });
            setIncludedList('');
            setExcludedList('');
            setEditingPlan(null);
        }
        setIsFormOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        const includedArr = includedList.split('\n').map(i => i.trim()).filter(i => i);
        const excludedArr = excludedList.split('\n').map(i => i.trim()).filter(i => i);
        
        const payload = {
            ...formData,
            action: 'save_plan',
            features_included_json: JSON.stringify(includedArr),
            features_excluded_json: JSON.stringify(excludedArr)
        };

        try {
            const res = await fetch('https://menu.searchisend.in/superadmin/v2/v2_manage_plans.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                setIsFormOpen(false);
                fetchPlans();
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toast.error("Save failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this plan? Restaurants currently using it might be affected.")) return;
        
        try {
            const res = await fetch('https://menu.searchisend.in/superadmin/v2/v2_manage_plans.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'delete_plan', id })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                fetchPlans();
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toast.error("Delete failed.");
        }
    };

    return (
        <SuperAdminLayout>
        <div className="container p-4 max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
                        <Settings size={24} className="text-blue-600" />
                        Manage SaaS Plans
                    </h1>
                    <p className="text-gray-600">Create and modify the pricing plans shown on your landing page and dashboard.</p>
                </div>
                <button 
                    onClick={() => handleOpenForm()}
                    className="mt-4 md:mt-0 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    <Plus size={18} /> Add New Plan
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12"><div className="loader inline-block"></div><p>Loading plans...</p></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {plans.map(plan => (
                        <div key={plan.id} className={`bg-white rounded-xl shadow-sm border p-5 relative ${parseInt(plan.is_popular) === 1 ? 'border-orange-500 shadow-orange-100' : 'border-gray-200'}`}>
                            {parseInt(plan.is_popular) === 1 && (
                                <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">POPULAR</div>
                            )}
                            <h3 className="text-xl font-bold mb-1">{plan.plan_name}</h3>
                            <div className="text-gray-500 text-sm mb-4 h-10">{plan.tagline}</div>
                            
                            <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
                                {parseInt(plan.is_monthly_active) === 1 && (
                                    <div className="flex justify-between mb-1">
                                        <span>{plan.days_monthly} Days:</span>
                                        <span className="font-semibold">₹{plan.price_monthly}</span>
                                    </div>
                                )}
                                {parseInt(plan.is_yearly_active) === 1 && (
                                    <div className="flex justify-between">
                                        <span>{plan.days_yearly} Days:</span>
                                        <span className="font-semibold">₹{plan.price_yearly}</span>
                                    </div>
                                )}
                            </div>

                            <div className="text-sm text-gray-600 mb-4 space-y-1">
                                <div><span className="font-medium text-gray-900">Orders:</span> {parseInt(plan.max_orders) > 900000 ? 'Unlimited' : plan.max_orders}/mo</div>
                                <div><span className="font-medium text-gray-900">Staff Limits:</span> {plan.max_staff}</div>
                                <div><span className="font-medium text-gray-900">AI Credits:</span> {plan.ai_credits}</div>
                            </div>

                            <div className="flex gap-2">
                                <button onClick={() => handleOpenForm(plan)} className="flex-1 flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 rounded-lg transition">
                                    <Pencil size={16} /> Edit
                                </button>
                                <button onClick={() => handleDelete(plan.id)} className="flex-none flex items-center justify-center gap-1 bg-red-50 hover:bg-red-100 text-red-600 px-3 py-2 rounded-lg transition">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isFormOpen && (
                <div className="fixed inset-0 bg-black/60 z-[999] flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl max-w-4xl w-full mt-10 mb-10 max-h-[85vh] overflow-y-auto shadow-2xl relative">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                            <h2 className="text-xl font-bold">{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
                            <button onClick={() => setIsFormOpen(false)} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                        </div>
                        <form onSubmit={handleSave} className="p-6">
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <div className="space-y-4">
                                    <h3 className="font-bold text-gray-800 border-b pb-2">Basic Info</h3>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Plan Name *</label>
                                        <input required type="text" className="w-full border rounded-lg p-2" value={formData.plan_name} onChange={e => setFormData({...formData, plan_name: e.target.value})} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tagline</label>
                                        <input type="text" className="w-full border rounded-lg p-2" value={formData.tagline} onChange={e => setFormData({...formData, tagline: e.target.value})} placeholder="e.g. For serious restaurants..." />
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer mt-2">
                                        <input type="checkbox" checked={Number(formData.is_popular) === 1} onChange={e => setFormData({...formData, is_popular: e.target.checked ? 1 : 0})} />
                                        <span className="text-sm font-medium text-orange-600">Mark as "Popular" on Landing Page</span>
                                    </label>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="font-bold text-gray-800 border-b pb-2">Pricing & Duration Options</h3>
                                    
                                    <div className="bg-blue-50 p-3 rounded-lg space-y-3">
                                        <label className="flex items-center gap-2 font-semibold">
                                            <input type="checkbox" checked={Number(formData.is_monthly_active) === 1} onChange={e => setFormData({...formData, is_monthly_active: e.target.checked ? 1 : 0})} /> Option A (Short-term)
                                        </label>
                                        {Number(formData.is_monthly_active) === 1 && (
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-xs text-gray-600">Price (₹)</label>
                                                    <input required type="number" step="0.01" className="w-full border rounded p-1.5 text-sm" value={formData.price_monthly} onChange={e => setFormData({...formData, price_monthly: e.target.value})} />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="text-xs text-gray-600">Duration (Days)</label>
                                                    <input required type="number" className="w-full border rounded p-1.5 text-sm" value={formData.days_monthly} onChange={e => setFormData({...formData, days_monthly: e.target.value})} />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="bg-green-50 p-3 rounded-lg space-y-3">
                                        <label className="flex items-center gap-2 font-semibold">
                                            <input type="checkbox" checked={Number(formData.is_yearly_active) === 1} onChange={e => setFormData({...formData, is_yearly_active: e.target.checked ? 1 : 0})} /> Option B (Long-term)
                                        </label>
                                        {Number(formData.is_yearly_active) === 1 && (
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-xs text-gray-600">Price (₹)</label>
                                                    <input required type="number" step="0.01" className="w-full border rounded p-1.5 text-sm" value={formData.price_yearly} onChange={e => setFormData({...formData, price_yearly: e.target.value})} />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="text-xs text-gray-600">Duration (Days)</label>
                                                    <input required type="number" className="w-full border rounded p-1.5 text-sm" value={formData.days_yearly} onChange={e => setFormData({...formData, days_yearly: e.target.value})} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <div className="space-y-4">
                                    <h3 className="font-bold text-gray-800 border-b pb-2">Resource Limits</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">Max Orders / mo</label>
                                            <input type="number" className="w-full border rounded-lg p-2" value={formData.max_orders} onChange={e => setFormData({...formData, max_orders: e.target.value})} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">Max Staff Users</label>
                                            <input type="number" className="w-full border rounded-lg p-2" value={formData.max_staff} onChange={e => setFormData({...formData, max_staff: e.target.value})} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">AI Credits Added</label>
                                            <input type="number" className="w-full border rounded-lg p-2" value={formData.ai_credits} onChange={e => setFormData({...formData, ai_credits: e.target.value})} />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <h3 className="font-bold text-gray-800 border-b pb-2">Premium Features (Gatekeeping)</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 bg-white shadow-sm">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded border-gray-300" checked={Number(formData.has_offer_popup) === 1} onChange={e => setFormData({...formData, has_offer_popup: e.target.checked ? 1 : 0})} />
                                            <span className="text-sm font-semibold text-gray-700">Offer Popup</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 bg-white shadow-sm">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded border-gray-300" checked={Number(formData.has_coupons) === 1} onChange={e => setFormData({...formData, has_coupons: e.target.checked ? 1 : 0})} />
                                            <span className="text-sm font-semibold text-gray-700">Coupon Engine</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 bg-white shadow-sm">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded border-gray-300" checked={Number(formData.has_push) === 1} onChange={e => setFormData({...formData, has_push: e.target.checked ? 1 : 0})} />
                                            <span className="text-sm font-semibold text-gray-700">Push Notifications</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 bg-white shadow-sm">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded border-gray-300" checked={Number(formData.has_loyalty_system) === 1} onChange={e => setFormData({...formData, has_loyalty_system: e.target.checked ? 1 : 0})} />
                                            <span className="text-sm font-semibold text-gray-700">Loyalty Program</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 bg-white shadow-sm">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded border-gray-300" checked={Number(formData.has_staff_management) === 1} onChange={e => setFormData({...formData, has_staff_management: e.target.checked ? 1 : 0})} />
                                            <span className="text-sm font-semibold text-gray-700">Staff Management</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 bg-white shadow-sm">
                                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded border-gray-300" checked={Number(formData.has_ad_monetization) === 1} onChange={e => setFormData({...formData, has_ad_monetization: e.target.checked ? 1 : 0})} />
                                            <span className="text-sm font-semibold text-gray-700">Ad Monetization</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <div className="space-y-2">
                                    <h3 className="font-bold text-gray-800 border-b pb-2">Landing Page: Included Features</h3>
                                    <p className="text-xs text-gray-500">Put each feature on a new line. These get a green checkmark.</p>
                                    <textarea 
                                        className="w-full border rounded-lg p-2 text-sm min-h-[150px]" 
                                        value={includedList} 
                                        onChange={e => setIncludedList(e.target.value)}
                                        placeholder="Unlimited Menu Items&#10;QR Code Templates&#10;Order Dashboard"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="font-bold text-gray-800 border-b pb-2">Landing Page: Excluded Features</h3>
                                    <p className="text-xs text-gray-500">Put each feature on a new line. These get a locked/crossed icon.</p>
                                    <textarea 
                                        className="w-full border rounded-lg p-2 text-sm min-h-[150px]" 
                                        value={excludedList} 
                                        onChange={e => setExcludedList(e.target.value)}
                                        placeholder="WhatsApp Ordering&#10;Coupons"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 sticky bottom-0 bg-white pt-4 border-t">
                                <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                                <button type="submit" disabled={submitting} className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                    {submitting ? 'Saving...' : <><Save size={18} /> Save Plan</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
        </SuperAdminLayout>
    );
};

export default ManageSaasPlans;
